import type { OpenDir, StatInfo } from '@mimir/common/types/vfs';

// This file contains type definitions meant to facilitate vfs' read and write
// operations across electron's context bridge. There are two separate mechanisms
// used for this:
// 1. An interface nearly identically to the normal vfs interface except for a
//    read method that returns a Uint8Array rather than populating a pass-by-reference
//    parameter. This allows us to implement the proper vfs interface on the client side
//    of the context bridge by just copying this returned Uint8Array into its parameter.
//
// 2. A message port connection directly between the application and the node context which
//    avoids cloning and serialisation for both the electron context bridge and the ipc
//    communication with the node context. To achieve this it uses a SharedArrayBuffer.
//
// The second implementation should yield better performance, particulartly for large payloads,
// but the first one works even before the message port has been opened and works with
// Uint8Arrays that aren't backed by SharedArrayBuffer.
//
//
// Mechanism 1 data path:
//
// Vfs.write -> vfs wrapper -> preload -> ipcRenderer.invoke -> main -> fs.write
//                          ^                                 ^
//  crossing contextBridge (structured-clone like)            structured-clone
//
//
// fs.read -> main -> ipcRenderer.invoke -> preload -> vfs wrapper -> Vfs.read
//                  ^                                ^
//  returns buffer which is structured-cloned        crossing contextBridge (structured-clone like)
//
//
// Mechanism 2 data path:
//
// Vfs.write -> port.write -> main -> fs.write
//            ^
//   Structured-clone, but only reference for SharedArrayBuffer 
//
// Vfs.read <- port.read <- main -> fs.read
//          ^
//   Structured-clone, but only reference for SharedArrayBuffer 

export interface IpcOpenFile {
  readonly openFileId: number,
  read(count: number, fileOffset: number): Promise<Uint8Array>,
  write(buf: Uint8Array, fileOffset: number): Promise<number>,
  close(): Promise<void>,
}

export interface IpcVfs {
    open(path: string, flags: string): Promise<IpcOpenFile>,
    openDir(path: string): Promise<OpenDir>,
    rename(oldPath: string, newPath: string): Promise<void>,
    stat(path: string): Promise<StatInfo>,
    unlink(path: string): Promise<boolean>,
    mkdir(dirPath: string): Promise<void>,
    rmdir(dirPath: string): Promise<void>,
}

// MessagePort protocol for zero-copy reads and writes.
//
// The buf field is typed as Uint8Array<SharedArrayBuffer> to avoid silent 
// data loss that would happen if an ordinary Uint8Array was used, which would
// bet structured-cloned and that clone would be the only thing being written to,
// leaving the original buffer unaffected (which would work for the write command,
// but not for read)

export interface VfsPortReadRequest {
  requestId: number,
  op: 'read',
  openFileId: number,
  buf: Uint8Array<SharedArrayBuffer>,
  fileOffset: number,
}

export interface VfsPortWriteRequest {
  requestId: number,
  op: 'write',
  openFileId: number,
  buf: Uint8Array<SharedArrayBuffer>,
  fileOffset: number,
}

export type VfsPortRequest = VfsPortReadRequest | VfsPortWriteRequest;

export interface VfsPortSerializedError {
  message: string,
  code: string | undefined,
  errno: number | undefined,
  syscall: string | undefined,
}

export interface VfsPortSuccessResponse {
  requestId: number,
  ok: true,
  bytesTransferred: number,
}

export interface VfsPortErrorResponse {
  requestId: number,
  ok: false,
  error: VfsPortSerializedError,
}

export type VfsPortResponse = VfsPortSuccessResponse | VfsPortErrorResponse;

// Following the electron documentation, the MessagePort needs to be transferred with a
// postMessage call on window, so we need to define some constants to keep that tidy.

// Sent from renderer to main to request a fresh MessagePort connection.
export const VFS_REQUEST_PORT_CHANNEL = 'vfs-request-port';

// Sent from main to renderer carrying the MessagePortMain in event.ports[0].
export const VFS_PORT_CHANNEL = 'vfs-port';

// window.postMessage tag used by the preload to forward the port from the
// isolated context to the main world.
export const VFS_PORT_WINDOW_MESSAGE_TYPE = '__mimir-vfs-port';
